/**
 * Job Access Guard — any paid tier (Nobility / Royalty / Imperial) per the
 * subscription matrix. Strict gate: access ends the moment the subscription
 * lapses, even on a job the user previously created. Existing Firestore
 * documents are preserved untouched — only edit/manage UI is gated.
 */

import React, { useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { useFirebaseAuth } from '@/hooks/use-firebase-auth';
import { useSubscription } from '@/hooks/use-subscription';
import { useToast } from '@/hooks/use-toast';

interface JobAccessGuardProps {
  children: React.ReactNode;
  /** Post-login return path, defaults to /my-jobs. */
  authReturnTo?: string;
}

export function JobAccessGuard({
  children,
  authReturnTo = '/my-jobs',
}: JobAccessGuardProps) {
  const { user, isLoading } = useFirebaseAuth();
  const { hasPaidAccess, loading: subscriptionLoading, planName } = useSubscription();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    if (!isLoading && !user) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to manage your job postings.',
        variant: 'destructive',
      });
      navigate(`/auth?returnTo=${encodeURIComponent(authReturnTo)}`);
    }
  }, [isLoading, user, authReturnTo, navigate, toast]);

  if (isLoading || !user) {
    return null;
  }

  // Keep children mounted while subscription is loading so multi-step edit
  // flows are not torn down when entitlement data finally arrives.
  if (subscriptionLoading) {
    return <>{children}</>;
  }

  if (hasPaidAccess) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="max-w-md text-center p-8">
        <h2 className="text-xl font-bold text-white mb-3">Subscription Required</h2>
        <p className="text-gray-400 mb-6">
          You need an active subscription to manage jobs. Your current plan
          ({planName || 'Free'}) does not include job management.
        </p>
        <div className="flex gap-3 justify-center">
          <Button
            variant="outline"
            onClick={() => window.history.back()}
            className="border-gray-600 text-gray-300"
          >
            Go Back
          </Button>
          <Button
            onClick={() => navigate('/subscription')}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            View Plans
          </Button>
        </div>
      </div>
    </div>
  );
}
